import { useLocation, useNavigate } from 'react-router';

import { useShallow } from 'zustand/react/shallow';

import { ChevronLeftIcon, CloseIcon, IconButton, MenuIcon, TopBar } from '@thefarmersfront/kitchen-mobile';

import { pageTitles } from '~/configs/headerConfig';
import { CONTRACT_TYPE } from '~/constants';
import { useGetLatestUserAndCommuteInfo } from '~/hooks';
import { useTokenStore, useUserInfoStore } from '~/store';

import KurlyroLogo from '../Icons/KurlyroLogo';
import SpecialMedicalBadge from '../SubmitSpecialMedical/SpecialMedicalBadge';

import styles from './Header.module.scss';

/**
 * Header 컴포넌트 props type 정의
 * @category Header
 */
export type TypeHeaderProps = {
  /** @default false - 드로어 사용가능 여부 */
  showDrawer?: boolean;
  /** @default false - 뒤로가기 아이콘 버튼 표시 유무 */
  showGoBack?: boolean;
  /** @default false - 앱 로고 아이콘 버튼 표시 유무 */
  showAppLogo: boolean;
  /** @default false - 닫기 아이콘 버튼 표시 유무 */
  showClose?: boolean;
  /** @param {MouseEvent} event 드로어 아이콘 버튼 클릭 이벤트 */
  onDrawerIconClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** @param {MouseEvent} event 뒤로가기 아이콘 버튼 클릭 이벤트 */
  onGoBackIconClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

/**
 * @description 헤더 컴포넌트
 * @param TypeHeaderProps
 * @returns - JSX.Element / Header 컴포넌트 반환
 */
const Header = ({
  showDrawer = false,
  showGoBack = false,
  showAppLogo = false,
  showClose = false,
  onDrawerIconClick = undefined,
  onGoBackIconClick = undefined,
}: TypeHeaderProps): React.JSX.Element => {
  const navigate = useNavigate();
  const { state, pathname } = useLocation();
  const accessToken = useTokenStore((state) => state.accessToken);
  const { contractType, getIsContractStatusCurrent } = useUserInfoStore(
    useShallow((state) => ({
      contractType: state.contractType,
      getIsContractStatusCurrent: state.actions.getIsContractStatusCurrent,
    }))
  );

  const getLatestUserAndCommuteInfo = useGetLatestUserAndCommuteInfo();

  const getPageName = (): string => {
    return pageTitles[pathname] || '';
  };

  const pageName = getPageName();

  const handleAppLogoClick = (): void => {
    if (!accessToken) {
      navigate('/login');
      return;
    }

    getLatestUserAndCommuteInfo();

    /** 계약 구분이 아르바이트인 경우 */
    if (contractType === CONTRACT_TYPE.SHORT) {
      const isCurrentContract = getIsContractStatusCurrent();

      if (!isCurrentContract) {
        navigate('/check-network', { replace: true });
      } else {
        navigate('/start-work', { replace: true });
      }
    } else {
      /** 계약 구분이 상용직인 경우 */
      navigate('/start-work', { replace: true });
    }
  };

  const handleCloseIconClick = (): void => {
    const isSearchPostCode = ['/contract/search-post-code', '/settings/search-post-code'].includes(pathname);

    if (isSearchPostCode) {
      navigate(state.pathname, { state: { ...state }, replace: true });
    }
  };

  return (
    <TopBar
      className={styles['header']}
      title={<HeaderTitle title={pageName} />}
      backgroundColor="transparent"
      leftField={
        <TopBarLeftFields
          showGoBack={showGoBack}
          showAppLogo={showAppLogo}
          showClose={showClose}
          onCloseIconClick={handleCloseIconClick}
          onGoBackIconClick={onGoBackIconClick}
          handleAppLogoClick={handleAppLogoClick}
        />
      }
      rightField={<TopBarRightFields showDrawer={showDrawer} onDrawerIconClick={onDrawerIconClick} />}
    />
  );
};

const HeaderTitle = ({ title }: { title: string }) => {
  const { pathname } = useLocation();
  if (pathname === '/submit-special-medical') {
    return (
      <div className={styles['header-title']}>
        {title}
        <SpecialMedicalBadge />
      </div>
    );
  }
  return title;
};

const TopBarLeftFields = ({
  showGoBack,
  showAppLogo,
  showClose,
  onCloseIconClick,
  onGoBackIconClick,
  handleAppLogoClick,
}: {
  showGoBack: boolean;
  showAppLogo: boolean;
  showClose: boolean;
  onCloseIconClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onGoBackIconClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleAppLogoClick: () => void;
}) => {
  return (
    <div className={styles['header-left']}>
      {showAppLogo && (
        <button className={styles['app-logo-button']} onClick={handleAppLogoClick}>
          <KurlyroLogo width={81} height={31} />
        </button>
      )}
      <>
        {showGoBack && (
          <IconButton size="large" color="secondary" onClick={onGoBackIconClick}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </>
      {showClose && (
        <IconButton size="large" color="secondary" onClick={onCloseIconClick}>
          <CloseIcon />
        </IconButton>
      )}
    </div>
  );
};

const TopBarRightFields = ({
  showDrawer,
  onDrawerIconClick,
}: {
  showDrawer: boolean;
  onDrawerIconClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  return (
    <div className={styles['header-right']}>
      {showDrawer && (
        <IconButton size="large" color="secondary" onClick={onDrawerIconClick}>
          <MenuIcon />
        </IconButton>
      )}
    </div>
  );
};

export default Header;
